import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { HelperText } from '@cbhq/cds-web/controls/HelperText';
import { VStack } from '@cbhq/cds-web/layout';
import type { TrayRefProps } from '@cbhq/cds-web/overlays/tray/Tray';
import { useToast } from '@cbhq/cds-web/overlays/useToast';
import { SectionHeader } from '@cbhq/cds-web/section-header/SectionHeader';
import { Text } from '@cbhq/cds-web/typography/Text';
import { useGetWorkspaceMembers } from '@cbhq/identity-api-hooks/account-management/fetch';

import { useOrganization } from '../../../contexts/OrganizationContext';
import { useUserProfile } from '../../../contexts/UserProfileContext';
import { EntityAccessFooter } from '../components/InviteTray/EntityAccessFooter';
import { InviteAccessBanner } from '../components/InviteTray/InviteAccessBanner';
import { InviteEmailSection } from '../components/InviteTray/InviteEmailSection';
import { InviteTray } from '../components/InviteTray/InviteTray';
import { MultiLEProductAccessGroup } from '../components/InviteTray/MultiLEProductAccessGroup';
import { ProductAccessFooter } from '../components/InviteTray/ProductAccessFooter';
import { ProductAccessGroup } from '../components/InviteTray/ProductAccessGroup';
import { PRODUCTS } from '../components/InviteTray/productCatalog';
import { useInviteEmailField } from '../components/InviteTray/useInviteEmailField';
import { UnsavedChangesAlert } from '../components/UnsavedChangesAlert';
import { useInviteSubmission } from '../useInviteSubmission';
import { useMultiLEProductAccessEntities } from '../useMultiLEProductAccessEntities';
import { useMultiLEProductSelection } from '../useMultiLEProductSelection';
import { useProductSelection } from '../useProductSelection';
import { useRoles } from '../utils/useRoles';

const messages = defineMessages({
  accessHeading: {
    id: 'Team.inviteTray.accessHeading',
    defaultMessage: 'Access',
    description: 'Heading for the product access section of the invite tray',
  },
  inviteSuccess: {
    id: 'Team.multiLEInviteTray.inviteSuccess',
    defaultMessage: 'Invite sent',
    description: 'Toast shown after a multi legal entity invite is successfully sent',
  },
  inviteError: {
    id: 'Team.multiLEInviteTray.inviteError',
    defaultMessage: 'Error sending invite',
    description: 'Toast shown when sending a multi legal entity invite fails',
  },
  productAccessRequiredError: {
    id: 'Team.inviteTray.productAccessRequiredError',
    defaultMessage: 'You must select at least one product',
    description: 'Inline error shown when no product access has been selected on submit',
  },
});

type MultiLEInviteTrayParams = {
  onClose: () => void;
};

export function MultiLEInviteTray({ onClose }: MultiLEInviteTrayParams) {
  const { formatMessage } = useIntl();
  const toast = useToast();
  const trayRef = useRef<TrayRefProps>(null);
  const [alertVisible, setAlertVisible] = useState(false);
  const { selectedOrganization } = useOrganization();
  const userProfile = useUserProfile();
  const currentUserEmail = userProfile?.email;
  const organizationId = selectedOrganization?.organizationId ?? '';

  const orgMembersData = useGetWorkspaceMembers(
    { organizationId },
    { skip: organizationId.length === 0 },
  );

  const {
    email,
    emailError,
    setEmailError,
    handleEmailChange,
    validate: validateEmail,
  } = useInviteEmailField({
    currentUserEmail,
    members: orgMembersData?.members,
    pendingInvites: orgMembersData?.pendingInvites,
  });
  const [accessError, setAccessError] = useState<string | null>(null);
  const [mode, setMode] = useState<'bulk' | 'independent'>('bulk');
  const { rolesByProduct } = useRoles();

  const localizedProducts = useMemo(
    () =>
      PRODUCTS.map(({ titleMessage, descriptionMessage, ...product }) => ({
        ...product,
        title: formatMessage(titleMessage),
        description: formatMessage(descriptionMessage),
      })),
    [formatMessage],
  );

  const legalEntityIds = useMemo(
    () =>
      (selectedOrganization?.legalEntities ?? [])
        .map((le) => le.legalEntityId)
        .filter((id): id is string => Boolean(id)),
    [selectedOrganization?.legalEntities],
  );

  const productKeys = useMemo(
    () => localizedProducts.map(({ productKey }) => productKey),
    [localizedProducts],
  );

  const handleCustomizeByEntity = useCallback(() => {
    setMode('independent');
    setAccessError(null);
  }, []);

  const handleApplyChangesInBulk = useCallback(() => {
    setMode('bulk');
    setAccessError(null);
  }, []);

  const {
    accessRows,
    selectedRoles,
    selectedCount,
    totalCount,
    allProductsSelected,
    handleSelectAll,
    handleDeselectAll,
  } = useProductSelection({ products: localizedProducts });

  const multiLEProductSelection = useMultiLEProductSelection({
    legalEntityIds,
    productKeys,
    rolesByProduct,
  });
  const {
    getLegalEntityRoles,
    hasAnySelection,
    handleSelectAllEntities,
    handleDeselectAllEntities,
  } = multiLEProductSelection;

  const legalEntityRoles = useMemo(
    () => (mode === 'independent' ? getLegalEntityRoles() : undefined),
    [getLegalEntityRoles, mode],
  );

  const submissionLegalEntityIds = useMemo(
    () => (legalEntityRoles ? legalEntityRoles.map((le) => le.legalEntityId) : legalEntityIds),
    [legalEntityIds, legalEntityRoles],
  );

  const submissionSelectedRoles = useMemo(
    () => (mode === 'independent' ? [] : selectedRoles),
    [mode, selectedRoles],
  );

  const entities = useMultiLEProductAccessEntities({
    legalEntities: selectedOrganization?.legalEntities ?? [],
    localizedProducts,
    rolesByProduct,
    selection: multiLEProductSelection,
  });

  const hasAccessSelection = mode === 'independent' ? hasAnySelection : selectedRoles.length > 0;

  useEffect(() => {
    if (hasAccessSelection) {
      setAccessError(null);
    }
  }, [hasAccessSelection]);

  const hasUnsavedInvite = useMemo(
    () => email.length > 0 || hasAccessSelection,
    [email.length, hasAccessSelection],
  );

  const handleInviteSuccess = useCallback(() => {
    toast.show(formatMessage(messages.inviteSuccess));
    onClose();
  }, [formatMessage, onClose, toast]);

  const handleInviteFailure = useCallback(() => {
    toast.show(formatMessage(messages.inviteError), { variant: 'bgNegative' });
  }, [formatMessage, toast]);

  const { isSubmitting, submitInvite } = useInviteSubmission({
    organizationId,
    email,
    selectedRoles: submissionSelectedRoles,
    legalEntityIds: submissionLegalEntityIds,
    legalEntityRoles,
    onSuccess: handleInviteSuccess,
    onFailure: handleInviteFailure,
  });

  const handleInvite = useCallback(() => {
    const nextEmailError = validateEmail();
    const nextAccessError =
      !hasAccessSelection || submissionLegalEntityIds.length === 0
        ? formatMessage(messages.productAccessRequiredError)
        : null;

    setEmailError(nextEmailError);
    setAccessError(nextAccessError);

    if (nextEmailError != null || nextAccessError != null) {
      return;
    }

    void submitInvite();
  }, [
    formatMessage,
    hasAccessSelection,
    setEmailError,
    submissionLegalEntityIds.length,
    submitInvite,
    validateEmail,
  ]);

  const handleBeforeClose = useCallback(() => {
    if (hasUnsavedInvite) {
      setAlertVisible(true);
      return;
    }
    trayRef.current?.close();
  }, [hasUnsavedInvite]);

  const handleKeepEditing = useCallback(() => {
    setAlertVisible(false);
  }, []);

  const handleDiscard = useCallback(() => {
    setAlertVisible(false);
    trayRef.current?.close();
  }, []);

  return (
    <>
      <InviteTray
        trayRef={trayRef}
        onClose={onClose}
        onInvite={handleInvite}
        beforeClose={handleBeforeClose}
        isSubmitting={isSubmitting}
      >
        <InviteEmailSection
          email={email}
          onEmailChange={handleEmailChange}
          errorMessage={emailError}
        />

        <VStack width="100%" gap={2}>
          <SectionHeader
            title={<Text font="headline">{formatMessage(messages.accessHeading)}</Text>}
            paddingX={0}
            paddingY={0}
          />
          <InviteAccessBanner
            mode={mode}
            entityCount={legalEntityIds.length}
            onCustomizeByEntity={handleCustomizeByEntity}
            onApplyChangesInBulk={handleApplyChangesInBulk}
          />
          {mode === 'independent' ? (
            <MultiLEProductAccessGroup
              entities={entities}
              footer={
                <EntityAccessFooter
                  entities={entities}
                  onSelectAll={handleSelectAllEntities}
                  onDeselectAll={handleDeselectAllEntities}
                />
              }
            />
          ) : (
            <ProductAccessGroup
              rows={accessRows}
              footer={
                <ProductAccessFooter
                  selectedCount={selectedCount}
                  totalCount={totalCount}
                  allSelected={allProductsSelected}
                  onSelectAll={handleSelectAll}
                  onDeselectAll={handleDeselectAll}
                />
              }
            />
          )}
          {accessError != null && <HelperText color="fgNegative">{accessError}</HelperText>}
        </VStack>
      </InviteTray>
      <UnsavedChangesAlert
        variant="invite"
        visible={alertVisible}
        onKeepEditing={handleKeepEditing}
        onDiscard={handleDiscard}
      />
    </>
  );
}
